import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Page, PageBody, PageHeader, Panel } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Checkbox, Field, Input, MoneyInput, Select, Switch } from '@/ui/Field';
import { Badge } from '@/ui/Badge';
import { api, errorMessage } from '@/lib/api/client';
import { humanize, marginPct, percent } from '@/lib/format';
import type {
  BrandSummary,
  DepartmentListResponse,
  VendorListResponse,
} from '@/lib/api/types';

/* Create a product.

   This screen is also the landing pad for the invoice-import flow: an unmatched
   invoice line links here with everything it already knows in the query string,
   so the operator confirms rather than retypes. When a vendor came with the
   prefill we create the vendor link too — as a SECOND call, because
   POST /products has no vendor field. If that second call fails the product
   still exists, and saying so plainly beats pretending the whole thing failed. */

const SIZE_UNITS = [
  'ct',
  'ea',
  'ft',
  'g',
  'gal',
  'in',
  'kg',
  'l',
  'lb',
  'ml',
  'oz',
  'pc',
  'pk',
  'pt',
  'qt',
] as const;
const STORAGE_TYPES = ['shelf', 'refrigerated', 'frozen'] as const;

interface NewProductSearch {
  name: string;
  /** Integer cents, as everywhere else in this API. */
  cost: number | null;
  barcode: string;
  vendorSku: string;
  unitsPerCase: number | null;
  caseCost: number | null;
  vendorId: number | null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
function asInt(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  return null;
}

export const Route = createFileRoute('/_app/products_/new')({
  staticData: { crumb: 'New product' },
  // Every field is prefill — a bare `/products/new` is the normal case, so the
  // link-side input type stays open while the screen reads a fully-defaulted
  // object.
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput): NewProductSearch => ({
    name: asString(s.name),
    cost: asInt(s.cost),
    barcode: asString(s.barcode),
    vendorSku: asString(s.vendorSku),
    unitsPerCase: asInt(s.unitsPerCase),
    caseCost: asInt(s.caseCost),
    vendorId: asInt(s.vendorId),
  }),
  component: NewProductScreen,
});

interface CreatedProduct {
  id: number;
  name: string;
}

function NewProductScreen() {
  const prefill = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(prefill.name);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [brandId, setBrandId] = useState<number | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(prefill.cost);
  const [barcode, setBarcode] = useState(prefill.barcode);
  const [sizeValue, setSizeValue] = useState<number | null>(null);
  const [sizeUnit, setSizeUnit] = useState('');
  const [storageType, setStorageType] = useState<string>('shelf');
  const [taxable, setTaxable] = useState(false);
  const [ebtEligible, setEbtEligible] = useState(true);
  const [wicEligible, setWicEligible] = useState(false);
  const [soldByWeight, setSoldByWeight] = useState(false);
  const [active, setActive] = useState(true);

  const [vendorSku, setVendorSku] = useState(prefill.vendorSku);
  const [unitsPerCase, setUnitsPerCase] = useState<number | null>(prefill.unitsPerCase);
  const [caseCost, setCaseCost] = useState<number | null>(prefill.caseCost);

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
    staleTime: 5 * 60_000,
  });
  const vendors = useQuery({
    queryKey: ['vendors', { pageSize: 500 }],
    queryFn: () => api.get<VendorListResponse>('/vendors', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
    enabled: prefill.vendorId !== null,
  });

  const vendorName =
    prefill.vendorId === null
      ? null
      : ((vendors.data?.data ?? []).find((v) => v.id === prefill.vendorId)?.name ??
        `Vendor ${prefill.vendorId}`);

  const create = useMutation({
    mutationFn: async () => {
      const product = await api.post<CreatedProduct>('/products', {
        name: name.trim(),
        price: price ?? 0,
        cost,
        departmentId,
        brandId,
        priceType: 'fixed',
        taxable,
        ebtEligible,
        wicEligible,
        soldByWeight,
        storageType,
        active,
        sizeValue,
        sizeUnit: sizeUnit || null,
        barcodes: barcode.trim()
          ? [{ code: barcode.trim(), type: 'upc', isPrimary: true }]
          : [],
      });

      // The vendor link is a separate record and a separate route. It must not
      // be able to lose us the product we just created.
      let vendorLinkError: string | null = null;
      if (prefill.vendorId !== null) {
        try {
          await api.post(`/vendors/${prefill.vendorId}/items`, {
            productId: product.id,
            vendorSku: vendorSku.trim() || null,
            unitsPerCase,
            caseCost,
            unitCost: cost,
            isPreferred: false,
          });
        } catch (err) {
          vendorLinkError = errorMessage(err);
        }
      }
      return { product, vendorLinkError };
    },
    onSuccess: ({ product, vendorLinkError }) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
      if (vendorLinkError) {
        toast.error('Product created — vendor link failed', {
          description: `${product.name} exists, but it was not linked to ${vendorName}. Add the link from the vendor's catalog. (${vendorLinkError})`,
          duration: 15_000,
        });
      } else {
        toast.success(`${product.name} created`, {
          description: prefill.vendorId !== null ? `Linked to ${vendorName}.` : undefined,
        });
      }
      void navigate({ to: '/products/$id', params: { id: String(product.id) } });
    },
    onError: (err) =>
      toast.error('Could not create product', { description: errorMessage(err) }),
  });

  const valid = name.trim().length > 0 && price !== null;
  const margin = price === null || cost === null ? null : marginPct(price, cost);

  return (
    <Page>
      <PageHeader
        eyebrow="Catalog"
        title="New product"
        subtitle={
          prefill.vendorId !== null
            ? 'Prefilled from an invoice line — confirm what the parser read.'
            : undefined
        }
        actions={
          <>
            <Button size="sm" icon={<ArrowLeft className="size-3.5" />} asChild>
              <Link to="/products">Cancel</Link>
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!valid}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create product
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="mx-auto grid max-w-4xl gap-3">
          <Panel title="Identity">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" required className="sm:col-span-2">
                <Input
                  autoFocus
                  size="sm"
                  value={name}
                  placeholder="What the operator will search for"
                  onChange={(e) => setName(e.currentTarget.value)}
                />
              </Field>
              <Field label="Department">
                <Select
                  size="sm"
                  value={departmentId ?? ''}
                  onChange={(e) =>
                    setDepartmentId(
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option value="">—</option>
                  {(departments.data?.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Brand">
                <Select
                  size="sm"
                  value={brandId ?? ''}
                  onChange={(e) =>
                    setBrandId(
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option value="">—</option>
                  {(brands.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Barcode"
                hint="Becomes the primary code"
                className="sm:col-span-2"
              >
                <Input
                  size="sm"
                  mono
                  value={barcode}
                  placeholder="Scan or type…"
                  onChange={(e) => setBarcode(e.currentTarget.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Pricing">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Retail price" required>
                <MoneyInput size="sm" value={price} onValueChange={setPrice} />
              </Field>
              <Field label="Unit cost">
                <MoneyInput size="sm" value={cost} onValueChange={setCost} />
              </Field>
              <Field label="Margin">
                <div className="flex h-7 items-center px-1 font-mono text-sm tabular-nums">
                  {margin === null ? (
                    <span className="text-ink-faint">—</span>
                  ) : (
                    <span
                      className={
                        margin < 0
                          ? 'text-negative'
                          : margin < 10
                            ? 'text-warn'
                            : 'text-ink'
                      }
                    >
                      {percent(margin)}
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Size">
                <Input
                  size="sm"
                  mono
                  inputMode="decimal"
                  value={sizeValue ?? ''}
                  onChange={(e) =>
                    setSizeValue(
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Unit">
                <Select
                  size="sm"
                  value={sizeUnit}
                  onChange={(e) => setSizeUnit(e.currentTarget.value)}
                >
                  <option value="">—</option>
                  {SIZE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Storage">
                <Select
                  size="sm"
                  value={storageType}
                  onChange={(e) => setStorageType(e.currentTarget.value)}
                >
                  {STORAGE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Panel>

          <Panel title="Flags">
            <div className="grid gap-2 sm:grid-cols-2">
              <Checkbox checked={taxable} onCheckedChange={setTaxable} label="Taxable" />
              <Checkbox
                checked={ebtEligible}
                onCheckedChange={setEbtEligible}
                label="EBT eligible"
              />
              <Checkbox
                checked={wicEligible}
                onCheckedChange={setWicEligible}
                label="WIC eligible"
              />
              <Checkbox
                checked={soldByWeight}
                onCheckedChange={setSoldByWeight}
                label="Sold by weight"
              />
            </div>
            <div className="border-line-soft mt-3 border-t pt-3">
              <Switch
                checked={active}
                onCheckedChange={setActive}
                label={active ? 'Active on the register' : 'Created inactive'}
              />
            </div>
          </Panel>

          {prefill.vendorId !== null ? (
            <Panel
              title="Vendor link"
              description="Created as a second step after the product itself."
              actions={<Badge tone="info">{vendorName}</Badge>}
            >
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Vendor SKU">
                  <Input
                    size="sm"
                    mono
                    value={vendorSku}
                    onChange={(e) => setVendorSku(e.currentTarget.value)}
                  />
                </Field>
                <Field label="Units per case">
                  <Input
                    size="sm"
                    mono
                    inputMode="numeric"
                    value={unitsPerCase ?? ''}
                    onChange={(e) =>
                      setUnitsPerCase(
                        e.currentTarget.value === ''
                          ? null
                          : Number(e.currentTarget.value)
                      )
                    }
                  />
                </Field>
                <Field label="Case cost">
                  <MoneyInput size="sm" value={caseCost} onValueChange={setCaseCost} />
                </Field>
              </div>
            </Panel>
          ) : null}
        </div>
      </PageBody>
    </Page>
  );
}
